import React, { useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  RouteObject
} from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

import Welcome from './Welcome';
import Login from './Login';
import Register from './Register';
import ConfirmEmail from './ConfirmEmail';
import NotFound from './NotFound';
import AccountDetails from './AccountDetails';
import Sessions from './Sessions';
import ForgottenPassword from './ForgottenPassword';
import ResetPassword from './ResetPassword';
import Terms from './Terms';
import Privacy from './Privacy';
import BasicLayout from '../layouts/BasicLayout';
import AuthenticatedLayout from '../layouts/AuthenticatedLayout';
import PolicyLayout from '../layouts/PolicyLayout';
import Users from './admin/Users';
import AccountLayout from '../layouts/AccountLayout';
import DeleteAccount from './DeleteAccount';
import ChangePasswordPage from './ChangePasswordPage';
import ChangeEmailAddressPage from './ChangeEmailAddressPage';
import MFA from './MFA';
import LoginMFA from './LoginMFA';
import LoginMFARecoveryCode from './LoginMFARecoveryCode';
import Showings from './Showings';
import Teams from './Teams';
import TeamMembers from './TeamMembers';
import ConfirmInvite from './ConfirmInvite';
import PresentShowing from './PresentShowing';
import Event from './Event';
import DefaultThemeDetails from './DefaultThemeDetails';
import CreateNewEvent from '../components/CreateNewEvent';
import RedirectToDashboard from '../components/RedirectToDashboard';
import { getCsrfToken } from '../services/auth.service';
import Help from './Help';
import { RequireFeature } from '../contexts/RequireFeature';
import AccountSubscription from './AccountSubscription';
import Contact from './Contact';
import Refund from './Refund';
import CheckoutWrapper from '../components/checkout/CheckoutWrapper';
import CheckoutReturn from '../components/checkout/CheckoutReturn';

const RouterSetup: React.FC = () => {
  const { isLoggedIn, isAdmin } = useAuth();

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        await getCsrfToken();
      } catch (error) {
        console.error('Failed to fetch CSRF token:', error);
      }
    };

    fetchCsrfToken();
  }, []);

  const routes: RouteObject[] = [
    {
      element: <PolicyLayout allowPublic={true} />,
      children: [
        { path: '/Policies/terms-of-use', element: <Terms /> },
        { path: '/Policies/privacy-policy', element: <Privacy /> },
        { path: '/contact', element: <Contact /> },
        { path: '/Policies/refund-and-cancellation', element: <Refund /> }
      ]
    },
    {
      element: <BasicLayout allowPublic={true} />,
      children: [{ path: '/', element: <Welcome /> }]
    },
    {
      element: <BasicLayout />,
      children: [
        {
          path: '/Account/Login',
          element: isLoggedIn ? <RedirectToDashboard /> : <Login />
        },
        { path: '/Account/LoginMFA', element: <LoginMFA /> },
        {
          path: '/Account/LoginMFARecoveryCode',
          element: <LoginMFARecoveryCode />
        },
        {
          path: '/Account/Register',
          element: isLoggedIn ? <RedirectToDashboard /> : <Register />
        },
        { path: '/Account/ConfirmEmail', element: <ConfirmEmail /> },
        { path: '/Account/ConfirmInvite', element: <ConfirmInvite /> },
        { path: '/Account/ForgottenPassword', element: <ForgottenPassword /> },
        { path: '/Account/ResetPassword', element: <ResetPassword /> }
      ]
    },
    {
      element: <AccountLayout />,
      children: [
        { path: '/Account/Details', element: <AccountDetails /> },
        { path: '/Account/Delete', element: <DeleteAccount /> },
        { path: '/Account/MFA', element: <MFA /> },
        { path: '/Account/Password', element: <ChangePasswordPage /> },
        {
          path: '/Account/ChangeEmailAddress',
          element: <ChangeEmailAddressPage />
        },
        { path: '/Account/terms-of-use', element: <Terms /> },
        { path: '/Account/privacy-policy', element: <Privacy /> },
        { path: '/Account/Subscription', element: <AccountSubscription /> },
        { path: '/defaults/theme', element: <DefaultThemeDetails /> },
        { path: '/checkout', element: <CheckoutWrapper /> },
        { path: '/checkout/return', element: <CheckoutReturn /> }
      ]
    },
    {
      element: <AuthenticatedLayout hasMenu={true} />,
      children: [
        { path: '/Sessions/:reference', element: <Event /> },
        {
          path: '/Sessions/:sessionReference/Present/:showingReference',
          element: <PresentShowing />
        }
      ]
    },
    {
      element: <AuthenticatedLayout fullPageForm={true} />,
      children: [{ path: '/Sessions/New', element: <CreateNewEvent /> }]
    },
    {
      element: <AuthenticatedLayout />,
      children: [
        { path: '/dashboard', element: <Sessions /> },
        { path: '/help', element: <Help /> },
        {
          path: '/Sessions/:reference/Showings',
          element: (
            <RequireFeature feature="MultipleShowings">
              <Showings />
            </RequireFeature>
          )
        },
        { path: '/Teams', element: <Teams /> },
        { path: '/Teams/:reference', element: <TeamMembers /> },
        ...(isAdmin ? [{ path: '/Admin/users', element: <Users /> }] : []),
        { path: '*', element: <NotFound /> }
      ]
    }
  ];

  const router = createBrowserRouter(routes);

  return <RouterProvider router={router} />;
};

export default RouterSetup;
